import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, ChevronRight, RotateCw } from "lucide-react";

import { Button } from "../ui/button";
import { useLanguage } from "../../contexts/LanguageContext";

const VISIBLE_ROWS = 6;

const recipientsLabel = ({ groupNames, athleteCount, unknownGroups }, t) => {
	const parts = [];
	if (groupNames.length > 0) parts.push(groupNames.join(", "));
	if (athleteCount > 0) {
		parts.push(
			athleteCount === 1
				? t("todayCardAthleteOne")
				: t("todayCardAthletes").replace("{count}", athleteCount),
		);
	} else if (unknownGroups > 0) {
		parts.push(
			unknownGroups === 1
				? t("todayCardGroupOne")
				: t("todayCardGroups").replace("{count}", unknownGroups),
		);
	}
	return parts;
};

/**
 * Il blocco «Oggi» della dashboard coach: un programma per riga, quelli che
 * oggi hanno una giornata consegnata agli atleti. Un tocco apre la vista
 * atleta (`AthleteViewPreview`) gia' su oggi. Solo elenco e stati: le righe le
 * calcola `buildTodayProgramEntries` (`lib/todayPrograms.js`).
 * Spec: docs/coach-today/01-analisi.md.
 */
export default function TodayProgramsCard({
	entries,
	loading,
	error,
	onRetry,
	onOpen,
	todayLabel,
	className = "",
}) {
	const { t } = useLanguage();
	const [expanded, setExpanded] = useState(false);

	const rows = Array.isArray(entries) ? entries : [];
	const hidden = Math.max(rows.length - VISIBLE_ROWS, 0);
	const visibleRows = expanded ? rows : rows.slice(0, VISIBLE_ROWS);

	return (
		<section
			aria-labelledby="dashboard-today-title"
			className={`bg-card border border-border rounded-sm p-4 md:p-6 flex flex-col gap-3 ${className}`}
		>
			<div className="flex items-baseline justify-between gap-3 flex-wrap">
				<div className="flex items-center gap-2">
					<CalendarDays className="w-5 h-5 text-volt" />
					<h2
						id="dashboard-today-title"
						className="label-tactical text-muted-foreground"
					>
						{t("todayCardTitle")}
					</h2>
					<span className="text-xs text-muted-foreground">
						· {todayLabel}
					</span>
				</div>
				{!loading && !error && rows.length > 0 && (
					<p className="hidden md:block text-xs text-muted-foreground">
						{t("todayCardOpenHint")}
					</p>
				)}
			</div>

			{loading ? (
				<div className="divide-y divide-border border-t border-border">
					{[0, 1].map((key) => (
						<div key={key} className="py-3 min-h-[56px] space-y-2">
							<div className="h-4 w-2/5 bg-secondary rounded-sm animate-pulse" />
							<div className="h-3 w-3/5 bg-secondary/70 rounded-sm animate-pulse" />
						</div>
					))}
				</div>
			) : error ? (
				<div className="border-t border-border pt-3 flex items-center justify-between gap-3 flex-wrap">
					<p className="text-sm text-muted-foreground">{t("todayCardError")}</p>
					<Button
						type="button"
						variant="outline"
						size="sm"
						className="rounded-sm"
						onClick={onRetry}
					>
						<RotateCw className="w-3.5 h-3.5 mr-1.5" />
						{t("todayCardRetry")}
					</Button>
				</div>
			) : rows.length === 0 ? (
				<div className="border-t border-border pt-3 flex items-center justify-between gap-3 flex-wrap">
					<p className="text-sm text-muted-foreground">{t("todayCardEmpty")}</p>
					<Link
						to="/programs"
						className="text-sm text-volt hover:underline underline-offset-4"
					>
						{t("todayCardGoToPrograms")}
					</Link>
				</div>
			) : (
				<>
					<ul className="divide-y divide-border border-t border-border">
						{visibleRows.map((entry) => {
							const detail = [
								entry.isDated
									? entry.dayLabel || t("todayCardDayFallback")
									: t("todayCardUndatedHint"),
								...(entry.sessionCount > 1
									? [t("todayCardSessions").replace("{count}", entry.sessionCount)]
									: []),
								...recipientsLabel(entry.recipients, t),
							];
							return (
								<li key={entry.programId}>
									<button
										type="button"
										onClick={() => onOpen(entry)}
										className="group w-full text-left flex items-center gap-3 py-3 min-h-[56px] -mx-2 px-2 rounded-sm hover:bg-secondary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-volt/50 touch-manipulation"
									>
										<div className="min-w-0 flex-1">
											<div className="flex items-center gap-2 flex-wrap">
												<p className="font-heading text-base leading-snug break-words min-w-0">
													{entry.title}
												</p>
												{entry.isShared && (
													<span className="text-[10px] label-tactical border border-acid/40 text-acid px-1.5 py-0.5 rounded-sm">
														{t("todayCardShared")}
													</span>
												)}
												{!entry.isDated && (
													<span className="text-[10px] label-tactical border border-border text-muted-foreground px-1.5 py-0.5 rounded-sm">
														{t("todayCardUndated")}
													</span>
												)}
											</div>
											<p className="mt-0.5 text-xs text-muted-foreground break-words">
												{detail.join(" · ")}
											</p>
										</div>
										<ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-volt shrink-0" />
									</button>
								</li>
							);
						})}
					</ul>
					{hidden > 0 && (
						<button
							type="button"
							onClick={() => setExpanded((value) => !value)}
							className="self-start text-sm text-volt hover:underline underline-offset-4 min-h-[44px]"
						>
							{expanded
								? t("todayCardShowLess")
								: t("todayCardShowMore").replace("{count}", hidden)}
						</button>
					)}
				</>
			)}
		</section>
	);
}
